import type {
  AssistantMessage,
  AssistantPresentationModel,
} from "@triedge/ai-assistant-ui";
import {
  ASSISTANT_ROUTE_FAMILIES,
  type AssistantRouteFamilyDefinition,
  type AssistantRouteFamilyId,
} from "../../config/ai-assistant/route-family-registry";
import type { AdmissionsAIContextualAction, AdmissionsCitation } from "./types";

export const ASSISTANT_RESPONSE_RENDER_FIXTURE_STORAGE_KEY =
  "ystnj.ai.response-render.fixture";
export const ASSISTANT_RESPONSE_RENDER_FIXTURE_QUERY_PARAM =
  "__assistantVisualFixture";

export type AssistantResponseVisualFixtureScenario =
  | "canonical"
  | "empty-chat"
  | "rich-response"
  | "streaming"
  | "error"
  | "toast"
  | "rate-limit-upload";

export type AssistantResponseVisualFixtureDetailKind =
  | "admissions-detail"
  | "financial-aid-detail"
  | "students-family-detail"
  | "parent-portal-detail"
  | null;

export type AssistantResponseVisualFixturePersona =
  | "staff"
  | "admin"
  | "parent";

export type AssistantResponseVisualFixtureCase = {
  routeFamilyId: AssistantRouteFamilyId;
  label: string;
  targetRoute: string;
  detailKind: AssistantResponseVisualFixtureDetailKind;
  persona: AssistantResponseVisualFixturePersona;
  runtimeProofMode: "enabled" | "policy_disabled";
};

export type AssistantResponseVisualFixtureSelection = {
  routeFamilyId: AssistantRouteFamilyId;
  scenarioId?: AssistantResponseVisualFixtureScenario;
};

export type AssistantResponseVisualFixtureRuntimeState = {
  routeFamilyId: AssistantRouteFamilyId;
  scenarioId: AssistantResponseVisualFixtureScenario;
  messages:
    | AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction>[]
    | null;
  conversationErrorMessage?: string | null;
  contextError?: string | null;
  toastNotification?: {
    message: string;
    tone?: "success" | "info" | "warning" | "error";
  } | null;
  inputBanner?: {
    kind?: "info" | "warning" | "error" | "rate-limit";
    title: string;
    description?: string;
    primaryActionLabel?: string;
    secondaryActionLabel?: string;
  } | null;
  stagedAttachments?: Array<{
    id: string;
    label: string;
    detail?: string;
    tone?: "neutral" | "success" | "warning" | "error";
  }>;
  pendingQuestion?: string | null;
  isLoading?: boolean;
};

const ASSISTANT_RESPONSE_VISUAL_FIXTURE_SCENARIOS =
  new Set<AssistantResponseVisualFixtureScenario>([
    "canonical",
    "empty-chat",
    "rich-response",
    "streaming",
    "error",
    "toast",
    "rate-limit-upload",
  ]);

const isAssistantResponseVisualFixtureScenario = (
  value: unknown,
): value is AssistantResponseVisualFixtureScenario =>
  typeof value === "string" &&
  ASSISTANT_RESPONSE_VISUAL_FIXTURE_SCENARIOS.has(
    value as AssistantResponseVisualFixtureScenario,
  );

const buildDetailKind = (
  family: AssistantRouteFamilyDefinition,
): AssistantResponseVisualFixtureDetailKind => {
  switch (family.id) {
    case "admissions":
      return "admissions-detail";
    case "financial-aid":
      return "financial-aid-detail";
    case "students-families":
      return "students-family-detail";
    case "parent-portal":
      return "parent-portal-detail";
    default:
      return null;
  }
};

const resolveFixturePersona = (
  family: AssistantRouteFamilyDefinition,
): AssistantResponseVisualFixturePersona => {
  switch (family.id) {
    case "admissions":
    case "financial-aid":
    case "settings":
      return "admin";
    case "parent-portal":
      return "parent";
    default:
      return "staff";
  }
};

export const ASSISTANT_RESPONSE_VISUAL_FIXTURE_CASES: AssistantResponseVisualFixtureCase[] =
  ASSISTANT_ROUTE_FAMILIES.filter(
    (family) => family.id !== "unknown" && family.id !== "payroll",
  ).map((family) => ({
    routeFamilyId: family.id,
    label: family.label,
    targetRoute:
      family.suggestedRoutes[0]?.path ??
      family.routePrefixes[0] ??
      "/school/admissions/dashboard",
    detailKind: buildDetailKind(family),
    persona: resolveFixturePersona(family),
    runtimeProofMode:
      family.id === "parent-portal" ? "policy_disabled" : "enabled",
  }));

const buildCitation = (
  family: AssistantRouteFamilyDefinition,
  index: number,
): AdmissionsCitation => ({
  table:
    family.module === "financial-aid"
      ? "FinancialAidApplications"
      : "Applications",
  key:
    family.module === "financial-aid"
      ? { applicationId: `${family.id}-application-${index + 1}` }
      : { id: `${family.id}-record-${index + 1}` },
  fieldPath: `${family.id}.summary.${index + 1}`,
  preview: `${family.label} fixture source ${index + 1}`,
  applicationCode: `${family.id
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .slice(0, 12)}-${String(index + 1).padStart(3, "0")}`,
  entityLabel: `${family.label} record ${index + 1}`,
  module: family.module,
});

const buildNavigateAction = (
  family: AssistantRouteFamilyDefinition,
  label: string,
  href: string,
  index: number,
): AdmissionsAIContextualAction => ({
  id: `${family.id}-navigate-${index + 1}`,
  label,
  type: "navigate",
  kind: "navigate",
  targetRoute: href,
  entityRef: {
    kind: family.entityLabel,
    id: `${family.id}-entity-${index + 1}`,
    label,
    module: family.module,
  },
  payload: {
    href,
    targetRoute: href,
    entityLabel: label,
    entityKind: family.entityLabel,
  },
});

const buildPresentation = (
  family: AssistantRouteFamilyDefinition,
): AssistantPresentationModel => {
  const primaryRoute =
    family.suggestedRoutes[0]?.path ??
    family.routePrefixes[0] ??
    "/school/admissions/dashboard";
  const secondaryRoute = family.suggestedRoutes[1]?.path ?? primaryRoute;

  return {
    version: "1.0.0",
    module: family.module,
    routeFamily: family.id,
    sections: [
      {
        type: "summary",
        text: `### ${family.label} response summary\nRoute family: ${family.label}\nHeader: Operator-ready answer\n${family.summary}`,
      },
      {
        type: "metric_grid",
        title: `${family.label} snapshot`,
        items: [
          {
            id: `${family.id}-metric-routes`,
            label: "Routes",
            value: String(family.suggestedRoutes.length || 1),
            detail: "Canonical route actions surfaced in the response",
            tone: "info",
          },
          {
            id: `${family.id}-metric-ops`,
            label: "Ops",
            value: String(family.operations.length),
            detail: "Named workflow operations in this route family",
            tone: "success",
          },
          {
            id: `${family.id}-metric-sources`,
            label: "Sources",
            value: String(family.baseDataSources.length),
            detail: "Grounding source families attached to this route",
            tone: "neutral",
          },
        ],
      },
      {
        type: "result_cards",
        title: `${family.label} exact targets`,
        cards: [
          {
            id: `${family.id}-result-1`,
            label:
              family.suggestedRoutes[0]?.label ?? `${family.label} overview`,
            eyebrow: `${family.label} primary`,
            meta: family.operations.slice(0, 2).join(" • "),
            preview: `Open the canonical ${family.label.toLowerCase()} route and continue from the owned shell workflow.`,
            primaryAction: {
              label: "Open route",
              href: primaryRoute,
            },
            secondaryAction: {
              label: "Review sources",
              href: secondaryRoute,
            },
            detailRows: [
              { label: "Entity", value: family.entityLabel },
              { label: "Sources", value: family.baseDataSources.join(", ") },
            ],
          },
        ],
      },
      {
        type: "checklist",
        title: "Verification checklist",
        items: [
          {
            id: `${family.id}-check-structure`,
            label: "Verify shared response anatomy",
            detail:
              "Summary, snapshot, route cards, sources, and diagnostics should stay in canonical order.",
            status: "ready",
          },
          {
            id: `${family.id}-check-routes`,
            label: "Confirm route actions stay canonical",
            detail:
              "Primary actions should open owned routes rather than hidden aliases or placeholder links.",
            status: "todo",
          },
          {
            id: `${family.id}-check-drawers`,
            label: "Keep drawers collapsed by default",
            detail:
              "Sources and diagnostics remain supporting surfaces, not dominant chrome.",
            status: "watch",
          },
        ],
      },
      {
        type: "formatted_body",
        title: "Checklist",
        text: [
          "ALERT: Verify the structured result cards, sources drawer, and diagnostics drawer before promotion.",
          "Scope note: This deterministic fixture proves render consistency, not live query freshness.",
          "- [ ] Confirm route-specific chrome stays consistent.",
          "- [ ] Confirm sources remain collapsed by default.",
          "1. Review the primary action card.",
          "2. Verify related links and diagnostics stay tucked away.",
        ].join("\n"),
      },
      {
        type: "sources",
        items: family.baseDataSources.slice(0, 3).map((source, index) => ({
          id: `${family.id}-source-${index + 1}`,
          label: source,
          meta: `${family.label} source`,
          preview: `Deterministic proof source for ${family.label.toLowerCase()} response rendering.`,
          title: `${family.label} source ${index + 1}`,
        })),
      },
      {
        type: "related_links",
        groups: [
          {
            id: family.id,
            label: `${family.label} routes`,
            items: family.suggestedRoutes.map((route, index) => ({
              id: `${family.id}-link-${index + 1}`,
              href: route.path,
              label: route.label,
              meta: `${family.label} canonical route`,
              preview: `Canonical ${family.label.toLowerCase()} route for render-proof inspection.`,
            })),
          },
        ],
      },
      {
        type: "diagnostics",
        diagnostics: {
          title: `${family.label} render diagnostics`,
          summary:
            "Render proof fixture is active for screenshot verification.",
          explanation:
            "This fixture must preserve shared section ordering, spacing, and drawer defaults on every assistant route family.",
          operatorGuidance:
            "Clear the fixture storage key after the proof run so live assistant history resumes normally.",
          targetRoute: primaryRoute,
          defaultOpen: false,
        },
      },
    ],
  };
};

const buildRichResponsePresentation = (
  family: AssistantRouteFamilyDefinition,
): AssistantPresentationModel => ({
  version: "1.0.0",
  module: family.module,
  routeFamily: family.id,
  sections: [
    {
      type: "summary",
      text: [
        `### ${family.label} operator summary`,
        "",
        `**Active scope:** ${family.label}`,
        "**Operator mode:** Deterministic runtime visual proof",
        "",
        `This fixture activates the full rich-response anatomy for ${family.label.toLowerCase()} without switching away from the served route.`,
      ].join("\n"),
    },
    {
      type: "table",
      title: `${family.label} table`,
      columns: [
        { id: "name", label: family.entityLabel },
        { id: "status", label: "Status" },
        { id: "owner", label: "Owner" },
        { id: "days", label: "Days open", align: "right" },
      ],
      rows: [
        {
          id: `${family.id}-row-1`,
          cells: ["Cohen family", "Needs follow-up", "Admissions", "3"],
        },
        {
          id: `${family.id}-row-2`,
          cells: ["Mandel family", "Interview ready", "Review team", "1"],
        },
        {
          id: `${family.id}-row-3`,
          cells: ["Levy family", "Missing documents", "Operations", "5"],
        },
      ],
      footerText: "Showing 3 runtime-proof rows",
      footerAction: {
        label: "Open canonical route",
        href:
          family.suggestedRoutes[0]?.path ??
          family.routePrefixes[0] ??
          "/school/admissions/dashboard",
      },
    },
    {
      type: "chart",
      title: `${family.label} chart`,
      chartTitle: "Follow-up load by status",
      items: [
        {
          id: `${family.id}-chart-1`,
          label: "Ready",
          value: 64,
          tone: "accent",
        },
        {
          id: `${family.id}-chart-2`,
          label: "Review",
          value: 52,
          tone: "primary",
        },
        {
          id: `${family.id}-chart-3`,
          label: "Blocked",
          value: 34,
          tone: "primary",
        },
      ],
      maxValue: 70,
    },
    {
      type: "metric_grid",
      title: `${family.label} metrics`,
      items: [
        {
          id: `${family.id}-metric-1`,
          label: "Queued",
          value: "18",
          detail: "Route-scoped items waiting for review",
          tone: "warning",
        },
        {
          id: `${family.id}-metric-2`,
          label: "Ready",
          value: "11",
          detail: "Families ready for the next step",
          tone: "success",
        },
        {
          id: `${family.id}-metric-3`,
          label: "Sources",
          value: String(family.baseDataSources.length),
          detail: "Grounding sources in scope",
          tone: "info",
        },
      ],
    },
    {
      type: "artifacts",
      title: "Artifacts",
      items: [
        {
          id: `${family.id}-artifact-1`,
          label: `${family.label} workbook`,
          badge: "Workbook",
          meta: `${family.id}-assistant-workbook.xlsx`,
          preview: `Shared workbook artifact for ${family.label.toLowerCase()} proof review.`,
          href: "https://example.com/runtime-proof-workbook.xlsx",
          actionLabel: "Open workbook",
        },
        {
          id: `${family.id}-artifact-2`,
          label: `${family.label} chart`,
          badge: "Chart",
          meta: `${family.id}-assistant-chart.json`,
          preview: `Chart artifact attached to the runtime proof for ${family.label.toLowerCase()}.`,
          href: "https://example.com/runtime-proof-chart.json",
          actionLabel: "Download chart artifact",
        },
      ],
    },
    {
      type: "checklist",
      title: "Next steps",
      items: [
        {
          id: `${family.id}-check-1`,
          label: "Confirm the transcript keeps the shared anatomy",
          detail:
            "Table, chart, metrics, artifacts, and markdown block should remain in the canonical order.",
          status: "ready",
        },
        {
          id: `${family.id}-check-2`,
          label: "Verify runtime state stays route-scoped",
          detail:
            "The proof fixture must not reintroduce tenant/session chrome into the prototype score path.",
          status: "watch",
        },
      ],
    },
    {
      type: "formatted_body",
      title: "Operator notes",
      text: [
        `ALERT: ${family.label} response proof is active.`,
        "Scope note: This is a deterministic served-runtime fixture, not a live data freshness claim.",
        "- [ ] Verify markdown spacing remains intact.",
        "- [ ] Verify rich-response widgets keep the shared borders and radii.",
        "1. Confirm the row actions stay collapsed by default.",
        "2. Confirm diagnostics stay secondary to the response body.",
      ].join("\n"),
    },
  ],
});

const buildAdmissionsPipelineBottleneckPresentation = (
  family: AssistantRouteFamilyDefinition,
): AssistantPresentationModel => ({
  version: "1.0.0",
  module: family.module,
  routeFamily: family.id,
  responseKind: "multi_artifact_insight",
  sections: [
    {
      type: "summary",
      text: [
        "Admissions pipeline bottlenecks need chart, table, and export review.",
        "",
        "**Largest stage:** Submitted - 65 applications",
        "**Highest dwell risk:** Interview Scheduled - 446.6 average days",
        "",
        "Conversion history is not present in this route snapshot, so conversion rates are marked as a source gap while stage count and dwell evidence stay inspectable.",
      ].join("\n"),
    },
    {
      type: "metric_grid",
      title: "Bottleneck signals",
      items: [
        { id: "submitted", label: "Submitted", value: "65", detail: "33.7% of applications", tone: "warning" },
        { id: "under-review", label: "Under review", value: "31", detail: "16.1% of applications", tone: "info" },
        { id: "interview", label: "Interview dwell", value: "446.6d", detail: "Average dwell exceeds 45 days", tone: "warning" },
        { id: "conversion-gap", label: "Conversion", value: "Gap", detail: "History absent from current snapshot", tone: "neutral" },
      ],
    },
    {
      type: "chart",
      title: "Stage chart",
      chartTitle: "Admissions pipeline bottlenecks by stage",
      items: [
        { id: "submitted-chart", label: "Submitted", value: 65, tone: "primary" },
        { id: "under-review-chart", label: "Review", value: 31, tone: "primary" },
        { id: "interview-chart", label: "Interview", value: 15, tone: "accent" },
        { id: "decision-chart", label: "Decision", value: 9, tone: "primary" },
        { id: "waitlisted-chart", label: "Waitlist", value: 5, tone: "primary" },
      ],
      maxValue: 70,
    },
    {
      type: "table",
      title: "Stage evidence",
      columns: [
        { id: "stage", label: "Stage" },
        { id: "applications", label: "Applications", align: "right" },
        { id: "share", label: "Share", align: "right" },
        { id: "avgDwell", label: "Avg dwell", align: "right" },
        { id: "source", label: "Source status" },
      ],
      rows: [
        { id: "submitted", cells: ["Submitted", "65", "33.7%", "318.2d", "Count + dwell available"] },
        { id: "under-review", cells: ["Under Review", "31", "16.1%", "274.4d", "Count + dwell available"] },
        { id: "interview", cells: ["Interview Scheduled", "15", "7.8%", "446.6d", "High dwell"] },
        { id: "decision", cells: ["Decision Pending", "9", "4.7%", "443.9d", "High dwell"] },
        { id: "waitlisted", cells: ["Waitlisted", "5", "2.6%", "441.6d", "High dwell"] },
        { id: "accepted-pending", cells: ["Accepted Pending Enrollment", "24", "12.4%", "97.3d", "Needs enrollment follow-up"] },
        { id: "submitted-docs", cells: ["Submitted missing docs", "13", "6.7%", "229.1d", "Document gap"] },
        { id: "new-inquiry", cells: ["New Inquiry", "11", "5.7%", "38.4d", "Early pipeline"] },
        { id: "tour-complete", cells: ["Tour Complete", "8", "4.1%", "64.2d", "Awaiting decision"] },
        { id: "screening", cells: ["Screening", "6", "3.1%", "58.0d", "Staff review"] },
        { id: "declined", cells: ["Declined", "4", "2.1%", "12.0d", "Terminal"] },
        { id: "withdrawn", cells: ["Withdrawn", "2", "1.0%", "21.5d", "Terminal"] },
        { id: "conversion-gap", cells: ["Conversion history", "0", "0.0%", "n/a", "Source gap"] },
      ],
      pagination: {
        mode: "always",
        pageSize: 8,
        itemLabel: "Stage",
      },
    },
    {
      type: "artifacts",
      title: "Pipeline exports",
      items: [
        {
          id: "admissions-bottlenecks-csv",
          label: "Stage bottlenecks CSV",
          kind: "csv",
          badge: "CSV",
          href: "data:text/csv;charset=utf-8,stage,applications,share,avgDwell",
          actionLabel: "Download CSV",
          sourceRef: "admissions.pipeline.bottlenecks",
        },
        {
          id: "admissions-bottlenecks-source-ledger",
          label: "Source ledger JSON",
          kind: "json",
          badge: "JSON",
          href: "data:application/json;charset=utf-8,%7B%22source%22%3A%22admissions.pipeline.bottlenecks%22%7D",
          actionLabel: "Download source ledger",
          sourceRef: "admissions.pipeline.bottlenecks",
        },
      ],
    },
    {
      type: "sources",
      title: "Sources",
      items: [
        {
          id: "admissions-pipeline-source",
          label: "Admissions applications aggregate",
          meta: "193 applications",
          preview: "Current-state route rows include stage counts and dwell; conversion history is unavailable.",
        },
      ],
    },
    {
      type: "confirmation",
      title: "",
      tone: "success",
      titleText: "Review bottleneck artifacts",
      summary: "Use the chart for the pattern, the table for exact stage evidence, and exports for offline review.",
      meta: "Deterministic visual fixture",
    },
  ],
});

const buildAdmissionsChartArtifactProofMessages = (
  family: AssistantRouteFamilyDefinition,
): AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction>[] => [
  {
    messageId: `${family.id}-rich-user`,
    role: "user",
    content:
      "Identify admissions pipeline bottlenecks by stage count, dwell time, and conversion rates.",
    timestamp: Date.now() - 1_000,
  },
  {
    messageId: `${family.id}-rich-assistant`,
    role: "assistant",
    content: "Admissions pipeline bottleneck multi-artifact proof fixture.",
    citations: family.baseDataSources
      .slice(0, 2)
      .map((_, index) => buildCitation(family, index)),
    metadata: {
      presentationChrome: "prototype-minimal",
      presentationVariant: "prototype-exact",
    },
    presentation: buildAdmissionsPipelineBottleneckPresentation(family),
    timestamp: Date.now(),
  },
];

const buildTuitionSnapshotPresentation = (
  family: AssistantRouteFamilyDefinition,
): AssistantPresentationModel => ({
  version: "1.0.0",
  module: family.module,
  routeFamily: family.id,
  sections: [
    {
      type: "summary",
      text: [
        "Review tuition account risk from the current school tuition dashboard.",
        "",
        "**Billed:** $186,000",
        "**Collected:** $139,500",
        "",
        "Outstanding balances are concentrated in overdue payment plans and need follow-up before month close.",
      ].join("\n"),
    },
    {
      type: "table",
      title: "Payment Follow-up",
      columns: [
        { id: "bucket", label: "Bucket" },
        { id: "families", label: "Families", align: "right" },
        { id: "balance", label: "Balance", align: "right" },
      ],
      rows: [
        { id: "current", cells: ["Current", "48", "$34,200"] },
        { id: "30-days", cells: ["30 days", "12", "$18,400"] },
        { id: "60-plus", cells: ["60+ days", "7", "$9,900"] },
      ],
      footerText: "Showing tuition aging buckets",
    },
    {
      type: "chart",
      title: "Tuition Trend",
      chartTitle: "Outstanding Balance by Aging Bucket",
      items: [
        { id: "tuition-current", label: "Current", value: 34, tone: "accent" },
        { id: "tuition-30", label: "30d", value: 18, tone: "primary" },
        { id: "tuition-60", label: "60d", value: 10, tone: "primary" },
        { id: "tuition-90", label: "90d", value: 7, tone: "primary" },
      ],
      maxValue: 40,
    },
    {
      type: "metric_grid",
      title: "Tuition Snapshot",
      items: [
        {
          id: "tuition-billed",
          label: "Billed",
          value: "$186,000",
          detail: "Total posted charges",
          tone: "neutral",
        },
        {
          id: "tuition-collected",
          label: "Collected",
          value: "$139,500",
          detail: "Payments received",
          tone: "success",
        },
        {
          id: "tuition-outstanding",
          label: "Outstanding",
          value: "$46,500",
          detail: "Open receivables",
          tone: "warning",
        },
        {
          id: "tuition-overdue",
          label: "Overdue",
          value: "$12,400",
          detail: "Past due balances",
          tone: "info",
        },
      ],
    },
    {
      type: "confirmation",
      title: "",
      tone: "info",
      titleText: "Tuition snapshot prepared",
      summary:
        "Metric cards should render as a balanced two-by-two grid in the narrow assistant panel.",
      meta: "Deterministic visual fixture",
    },
  ],
});

const buildTuitionSnapshotProofMessages = (
  family: AssistantRouteFamilyDefinition,
): AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction>[] => [
  {
    messageId: `${family.id}-rich-user`,
    role: "user",
    content:
      "Review tuition account risk from the current school tuition dashboard.",
    timestamp: Date.now() - 1_000,
  },
  {
    messageId: `${family.id}-rich-assistant`,
    role: "assistant",
    content: "Tuition snapshot proof fixture.",
    citations: family.baseDataSources
      .slice(0, 2)
      .map((_, index) => buildCitation(family, index)),
    metadata: {
      presentationChrome: "prototype-minimal",
      presentationVariant: "prototype-exact",
    },
    presentation: buildTuitionSnapshotPresentation(family),
    timestamp: Date.now(),
  },
];

const buildStreamingFixtureMessages = (
  family: AssistantRouteFamilyDefinition,
): AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction>[] => [
  {
    messageId: `${family.id}-streaming-user`,
    role: "user",
    content: `Stream the ${family.label.toLowerCase()} proof response.`,
    timestamp: Date.now() - 2_000,
  },
  {
    messageId: `${family.id}-streaming-assistant`,
    role: "assistant",
    content: [
      `Streaming ${family.label.toLowerCase()} proof response.`,
      "Keep the cursor visible while the text is revealing.",
      "Do not finalize the transcript before the visual inspector captures the message.",
    ].join(" "),
    metadata: {
      textReveal: {
        mode: "word-by-word",
        startDelayMs: 0,
        wordDelayMs: 260,
        cursor: true,
        revealStructuredSections: false,
      },
    },
    timestamp: Date.now(),
  },
];

export const buildAssistantResponseVisualFixtureMessages = (
  family: AssistantRouteFamilyDefinition,
): AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction>[] => {
  const primaryRoute =
    family.suggestedRoutes[0]?.path ??
    family.routePrefixes[0] ??
    "/school/admissions/dashboard";
  const secondaryRoute = family.suggestedRoutes[1]?.path ?? primaryRoute;
  return [
    {
      messageId: `${family.id}-fixture-user`,
      role: "user",
      content: `Show the canonical ${family.label.toLowerCase()} response format for this route.`,
      timestamp: Date.now() - 1000,
    },
    {
      messageId: `${family.id}-fixture-assistant`,
      role: "assistant",
      content: `Canonical ${family.label} assistant fixture response.`,
      citations: family.baseDataSources
        .slice(0, 2)
        .map((_, index) => buildCitation(family, index)),
      actions: [
        buildNavigateAction(
          family,
          family.suggestedRoutes[0]?.label ?? `${family.label} overview`,
          primaryRoute,
          0,
        ),
        buildNavigateAction(
          family,
          family.suggestedRoutes[1]?.label ?? `${family.label} related route`,
          secondaryRoute,
          1,
        ),
      ],
      presentation: buildPresentation(family),
      timestamp: Date.now(),
    },
  ];
};

const buildRichResponseFixtureMessages = (
  family: AssistantRouteFamilyDefinition,
): AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction>[] => {
  if (family.id === "admissions") {
    return buildAdmissionsChartArtifactProofMessages(family);
  }
  if (family.id === "tuition") {
    return buildTuitionSnapshotProofMessages(family);
  }
  return [
    {
      messageId: `${family.id}-rich-user`,
      role: "user",
      content: `Show the rich ${family.label.toLowerCase()} proof response.`,
      timestamp: Date.now() - 1_000,
    },
    {
      messageId: `${family.id}-rich-assistant`,
      role: "assistant",
      content: `${family.label} rich response proof fixture.`,
      citations: family.baseDataSources
        .slice(0, 2)
        .map((_, index) => buildCitation(family, index)),
      presentation: buildRichResponsePresentation(family),
      timestamp: Date.now(),
    },
  ];
};

export const parseAssistantResponseVisualFixtureSelection = (
  value: string | null | undefined,
): AssistantResponseVisualFixtureSelection | null => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed) as {
      routeFamilyId?: string;
      scenarioId?: string;
    };
    if (!parsed?.routeFamilyId) {
      return null;
    }
    const routeFamilyId = parsed.routeFamilyId as AssistantRouteFamilyId;
    return ASSISTANT_RESPONSE_VISUAL_FIXTURE_CASES.some(
      (entry) => entry.routeFamilyId === routeFamilyId,
    )
      ? {
          routeFamilyId,
          scenarioId: isAssistantResponseVisualFixtureScenario(
            parsed.scenarioId,
          )
            ? parsed.scenarioId
            : "canonical",
        }
      : null;
  } catch {
    return null;
  }
};

export const parseAssistantResponseVisualFixtureScenarioFromSearch = (
  search: string,
): AssistantResponseVisualFixtureScenario | null => {
  const params = new URLSearchParams(search);
  const scenario = params.get(ASSISTANT_RESPONSE_RENDER_FIXTURE_QUERY_PARAM);
  return isAssistantResponseVisualFixtureScenario(scenario) ? scenario : null;
};

export const resolveAssistantResponseVisualFixtureCase = (
  routeFamilyId: AssistantRouteFamilyId,
): AssistantResponseVisualFixtureCase | null =>
  ASSISTANT_RESPONSE_VISUAL_FIXTURE_CASES.find(
    (entry) => entry.routeFamilyId === routeFamilyId,
  ) ?? null;

export const resolveAssistantResponseVisualFixtureMessages = (
  routeFamilyId: AssistantRouteFamilyId,
):
  | AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction>[]
  | null => {
  const family = ASSISTANT_ROUTE_FAMILIES.find(
    (entry) => entry.id === routeFamilyId,
  );
  return family ? buildAssistantResponseVisualFixtureMessages(family) : null;
};

export const resolveAssistantResponseVisualFixtureState = (
  routeFamilyId: AssistantRouteFamilyId,
  scenarioId: AssistantResponseVisualFixtureScenario = "canonical",
): AssistantResponseVisualFixtureRuntimeState | null => {
  const family = ASSISTANT_ROUTE_FAMILIES.find(
    (entry) => entry.id === routeFamilyId,
  );
  if (!family) {
    return null;
  }

  switch (scenarioId) {
    case "canonical":
      return {
        routeFamilyId,
        scenarioId,
        messages: buildAssistantResponseVisualFixtureMessages(family),
      };
    case "empty-chat":
      return {
        routeFamilyId,
        scenarioId,
        messages: [],
        pendingQuestion: null,
        isLoading: false,
      };
    case "rich-response":
      return {
        routeFamilyId,
        scenarioId,
        messages: buildRichResponseFixtureMessages(family),
      };
    case "streaming":
      return {
        routeFamilyId,
        scenarioId,
        messages: buildStreamingFixtureMessages(family),
        isLoading: true,
      };
    case "error":
      return {
        routeFamilyId,
        scenarioId,
        messages: [
          {
            messageId: `${family.id}-error-user`,
            role: "user",
            content: `Retry the ${family.label.toLowerCase()} assistant request.`,
            timestamp: Date.now() - 1_000,
          },
        ],
        conversationErrorMessage:
          "Unable to connect right now. Retry the request in a moment.",
      };
    case "toast":
      return {
        routeFamilyId,
        scenarioId,
        messages: buildAssistantResponseVisualFixtureMessages(family),
        toastNotification: {
          message: "Thanks for your feedback!",
          tone: "success",
        },
      };
    case "rate-limit-upload":
      return {
        routeFamilyId,
        scenarioId,
        messages: buildAssistantResponseVisualFixtureMessages(family),
        inputBanner: {
          kind: "rate-limit",
          title: "Daily message limit reached",
          description:
            "Resets in 3h 22m. Upgrade your plan for unlimited access.",
          primaryActionLabel: "Upgrade plan",
          secondaryActionLabel: "Dismiss",
        },
        stagedAttachments: [
          {
            id: `${family.id}-upload-chip`,
            label: "admissions_roster.pdf",
            detail: "Upload failed",
            tone: "error",
          },
        ],
      };
    default:
      return null;
  }
};
